import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Meta Tags Component
 * 
 * Dynamically updates meta tags for SEO and social sharing.
 * Includes Open Graph, Twitter Cards, and standard meta tags.
 */

interface SEOMetaTagsProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
}

export const SEOMetaTags = ({
  title = 'Scope Manager | Steel, Misc Metals and Metal Panel Project Intelligence',
  description = 'AI-powered document management and project intelligence for steel fabrication companies. Upload specs, search with AI, and collaborate on technical documentation.',
  keywords = [
    'steel fabrication',
    'structural steel',
    'metal panels',
    'misc metals',
    'AISC',
    'NOMMA',
    'project management',
    'construction',
    'AI document search',
    'specification management',
    'RAG search',
    'technical documentation'
  ],
  image = 'https://lovable.dev/opengraph-image-p98pqg.png',
  url,
  type = 'website',
  author = 'Scope Manager',
  publishedTime,
  modifiedTime,
}: SEOMetaTagsProps) => {
  const location = useLocation();
  const currentUrl = url || `${window.location.origin}${location.pathname}`;
  const fullTitle = title.includes('Scope Manager') ? title : `${title} | Scope Manager`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set meta tag
    const setMetaTag = (selector: string, content: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement;
      if (!element) {
        element = document.createElement('meta');
        const attribute = selector.includes('[name=') ? 'name' : 'property';
        const value = selector.match(/["']([^"']+)["']/)?.[1] || '';
        element.setAttribute(attribute, value);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Standard meta tags
    setMetaTag('meta[name="description"]', description);
    setMetaTag('meta[name="keywords"]', keywords.join(', '));
    setMetaTag('meta[name="author"]', author);

    // Open Graph tags
    setMetaTag('meta[property="og:title"]', fullTitle);
    setMetaTag('meta[property="og:description"]', description);
    setMetaTag('meta[property="og:image"]', image);
    setMetaTag('meta[property="og:url"]', currentUrl);
    setMetaTag('meta[property="og:type"]', type);
    setMetaTag('meta[property="og:site_name"]', 'Scope Manager');
    setMetaTag('meta[property="og:locale"]', 'en_US');

    // Twitter Card tags
    setMetaTag('meta[name="twitter:card"]', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', fullTitle);
    setMetaTag('meta[name="twitter:description"]', description);
    setMetaTag('meta[name="twitter:image"]', image);
    setMetaTag('meta[name="twitter:site"]', '@scopemanager');
    setMetaTag('meta[name="twitter:creator"]', '@scopemanager');

    // Article-specific tags
    if (type === 'article') {
      if (publishedTime) {
        setMetaTag('meta[property="article:published_time"]', publishedTime);
      }
      if (modifiedTime) {
        setMetaTag('meta[property="article:modified_time"]', modifiedTime);
      }
      setMetaTag('meta[property="article:author"]', author);
    }

    // Additional SEO tags
    setMetaTag('meta[name="robots"]', 'index, follow');
    setMetaTag('meta[name="googlebot"]', 'index, follow');
    setMetaTag('meta[name="application-name"]', 'Scope Manager');
    
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

  }, [fullTitle, description, keywords, image, currentUrl, type, author, publishedTime, modifiedTime]);

  return null;
};
